import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Badge,
  Combobox,
  Group,
  Kbd,
  Loader,
  Modal,
  ScrollArea,
  Text,
  TextInput,
  useCombobox,
} from "@mantine/core";
import { useDebouncedValue, useHotkeys, useMediaQuery, useOs } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import {
  IconAlertTriangle,
  IconArrowsExchange,
  IconCar,
  IconCashBanknote,
  IconChartHistogram,
  IconClock,
  IconFileAnalytics,
  IconKey,
  IconLanguage,
  IconMapPin,
  IconMoon,
  IconRoute,
  IconSearch,
  IconShieldCheck,
  IconSun,
  IconTicket,
  IconUserPlus,
  IconUsers,
  IconUserShield,
  IconCoins,
  type TablerIcon,
} from "@tabler/icons-react";
import { consoleApi } from "../../api/console";
import { useAuth } from "../../auth/useAuth";
import { can, canAccess, type AdminPermission } from "../../auth/rbac";
import { useI18n } from "../../i18n/context";
import type { TranslationKey } from "../../i18n/en";
import { StatusBadge } from "../common/StatusBadge";
import { NAV } from "./nav";
import { useThemeSwitch } from "./useThemeSwitch";

interface Entry {
  id: string;
  label: string;
  hint?: string;
  icon: TablerIcon;
  keywords: string;
  run: () => void;
  right?: ReactNode;
}

interface ActionDef {
  id: string;
  labelKey: TranslationKey;
  icon: TablerIcon;
  permission: AdminPermission | null;
  keywords: string;
  to?: string;
  run?: "theme" | "language";
}

// Quick actions: things people do, not just places they go.
const ACTIONS: ActionDef[] = [
  { id: "onboard", labelKey: "search.action.onboardDriver", icon: IconUserPlus, permission: "drivers.onboard", to: "/drivers?new=1", keywords: "new driver register add onboard sajili dereva" },
  { id: "review", labelKey: "search.action.reviewDrivers", icon: IconShieldCheck, permission: "drivers.onboard", to: "/drivers?status=underApproval", keywords: "approve kyc documents pending review" },
  { id: "stuck", labelKey: "search.action.stuckTrips", icon: IconClock, permission: "live.view", to: "/live?filter=stuck", keywords: "stuck delayed late problem trips" },
  { id: "demand", labelKey: "search.action.demandMap", icon: IconMapPin, permission: "live.view", to: "/live?layer=demand", keywords: "demand supply heat hotspots where drivers needed" },
  { id: "reassign", labelKey: "search.action.reassign", icon: IconArrowsExchange, permission: "trips.intervene", to: "/live", keywords: "reassign cancel complete intervene trip" },
  { id: "sos", labelKey: "search.action.activeSos", icon: IconAlertTriangle, permission: "sos.view", to: "/sos", keywords: "sos emergency panic" },
  { id: "settle", labelKey: "search.action.recordSettlement", icon: IconCashBanknote, permission: "finance.manage", to: "/settlements", keywords: "record cash payment settle debt collect" },
  { id: "cash", labelKey: "search.action.cashReconciliation", icon: IconCoins, permission: "finance.view", to: "/finance?tab=cash", keywords: "reconciliation uncollected commission cash owed" },
  { id: "promo", labelKey: "search.action.createPromo", icon: IconTicket, permission: "promos.manage", to: "/coupons?new=1", keywords: "new promo coupon discount code" },
  { id: "leaderboard", labelKey: "search.action.driverLeaderboard", icon: IconChartHistogram, permission: "analytics.view", to: "/analytics?tab=drivers", keywords: "top drivers leaderboard scorecard acceptance utilisation" },
  { id: "monthly", labelKey: "search.action.monthlySummary", icon: IconFileAnalytics, permission: "reports.view", to: "/reports/monthly", keywords: "monthly report print pdf board owner" },
  { id: "admin", labelKey: "search.action.addAdmin", icon: IconUserShield, permission: "admins.manage", to: "/admins?new=1", keywords: "new admin staff user invite" },
  { id: "access", labelKey: "search.action.accessReview", icon: IconShieldCheck, permission: "audit.view", to: "/audit?tab=access", keywords: "rbac permissions access review roles" },
  { id: "password", labelKey: "search.action.changePassword", icon: IconKey, permission: null, to: "/account/password", keywords: "password security nenosiri" },
  { id: "theme", labelKey: "search.action.toggleTheme", icon: IconMoon, permission: null, run: "theme", keywords: "dark light mode theme night" },
  { id: "lang", labelKey: "search.action.switchLanguage", icon: IconLanguage, permission: null, run: "language", keywords: "language swahili kiswahili english lugha" },
];

/** Dispatch on window to focus the header search from anywhere. */
export const FOCUS_SEARCH_EVENT = "safiri:focus-search";

const RECENT_KEY = "safiri.admin.recentSearch";

function readRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function pushRecent(id: string) {
  try {
    const next = [id, ...readRecent().filter((r) => r !== id)].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // storage blocked: recents just aren't remembered
  }
}

function matches(entry: Entry, q: string): boolean {
  const hay = `${entry.label} ${entry.keywords}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => hay.includes(token));
}

/**
 * Always-visible "go to / do" search in the header. Empty → every module the
 * role can open (plus recents); typing → modules, quick actions and live
 * trip/driver/rider lookups. `/` or Ctrl/⌘+K focuses it from anywhere.
 */
export function HeaderSearch() {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const [mobileOpen, setMobileOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useI18n();

  const focus = () => {
    if (isMobile) setMobileOpen(true);
    else inputRef.current?.focus();
  };
  useHotkeys([["mod+K", focus]], []);
  useHotkeys([["/", focus]]);
  useEffect(() => {
    window.addEventListener(FOCUS_SEARCH_EVENT, focus);
    return () => window.removeEventListener(FOCUS_SEARCH_EVENT, focus);
  });

  if (isMobile) {
    return (
      <>
        <ActionIcon
          variant="default"
          size="lg"
          aria-label={t("search.placeholder")}
          onClick={() => setMobileOpen(true)}
        >
          <IconSearch size={18} />
        </ActionIcon>
        <Modal
          opened={mobileOpen}
          onClose={() => setMobileOpen(false)}
          withCloseButton={false}
          fullScreen
          padding="sm"
        >
          <SearchBox autoFocus alwaysOpen onDone={() => setMobileOpen(false)} />
        </Modal>
      </>
    );
  }
  return <SearchBox inputRef={inputRef} />;
}

function SearchBox({
  inputRef,
  autoFocus,
  alwaysOpen,
  onDone,
}: {
  inputRef?: React.RefObject<HTMLInputElement | null>;
  autoFocus?: boolean;
  alwaysOpen?: boolean;
  onDone?: () => void;
}) {
  const navigate = useNavigate();
  const os = useOs();
  const { admin } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { isDark, toggle: toggleTheme } = useThemeSwitch();
  const colorScheme = isDark ? "dark" : "light";
  const [query, setQuery] = useState("");
  const [debounced] = useDebouncedValue(query.trim(), 250);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });
  const role = admin?.role;

  const entities = useQuery({
    queryKey: ["ops-search", debounced],
    queryFn: () => consoleApi.search(debounced),
    enabled: debounced.length >= 2,
    staleTime: 15_000,
  });

  const go = (to: string) => {
    navigate(to);
  };

  const modules: Entry[] = useMemo(
    () =>
      NAV.filter((n) => canAccess(role, n.module)).map((n) => ({
        id: `nav:${n.module}`,
        label: t(n.labelKey),
        hint: t(`nav.section.${n.section}`),
        icon: n.icon,
        keywords: `${n.keywords ?? ""} ${n.to}`,
        run: () => go(n.to),
      })),
    [role, lang],
  );

  const actions: Entry[] = useMemo(
    () =>
      ACTIONS.filter((a) => a.permission === null || can(role, a.permission)).map((a) => ({
        id: `act:${a.id}`,
        label:
          a.run === "theme"
            ? t(colorScheme === "dark" ? "search.action.lightMode" : "search.action.darkMode")
            : a.run === "language"
              ? t(lang === "en" ? "search.action.useSwahili" : "search.action.useEnglish")
              : t(a.labelKey),
        icon: a.run === "theme" ? (colorScheme === "dark" ? IconSun : IconMoon) : a.icon,
        keywords: a.keywords,
        run: () => {
          if (a.run === "theme") toggleTheme();
          else if (a.run === "language") setLang(lang === "en" ? "sw" : "en");
          else if (a.to) go(a.to);
        },
      })),
    [role, lang, colorScheme],
  );

  const q = query.trim();
  const recentIds = readRecent();
  const byId = new Map([...modules, ...actions].map((e) => [e.id, e]));
  const recent = q ? [] : recentIds.map((id) => byId.get(id)).filter(Boolean) as Entry[];
  const moduleHits = q ? modules.filter((m) => matches(m, q)) : modules;
  const actionHits = q ? actions.filter((a) => matches(a, q)).slice(0, 8) : [];

  const data = entities.data;
  const entityGroups: { key: string; label: string; icon: TablerIcon; items: Entry[] }[] =
    q.length >= 2 && data
      ? [
          {
            key: "trips",
            label: t("search.group.trips"),
            icon: IconRoute,
            items: data.trips.map((h) => ({
              id: `trip:${h.id}`,
              label: h.title ?? h.id,
              hint: h.subtitle,
              icon: IconRoute,
              keywords: "",
              right: h.status ? <StatusBadge status={h.status} /> : undefined,
              run: () => go(`/trips/${h.id}`),
            })),
          },
          {
            key: "drivers",
            label: t("search.group.drivers"),
            icon: IconCar,
            items: data.drivers.map((h) => ({
              id: `driver:${h.id}`,
              label: h.title ?? h.id,
              hint: h.subtitle,
              icon: IconCar,
              keywords: "",
              right: h.status ? <StatusBadge status={h.status} /> : undefined,
              run: () => go(`/drivers/${h.id}`),
            })),
          },
          {
            key: "riders",
            label: t("search.group.riders"),
            icon: IconUsers,
            items: data.riders.map((h) => ({
              id: `rider:${h.id}`,
              label: h.title ?? h.id,
              hint: h.subtitle,
              icon: IconUsers,
              keywords: "",
              run: () => go(`/users/${h.id}`),
            })),
          },
        ].filter((g) => g.items.length > 0)
      : [];

  const all = new Map<string, Entry>();
  [...recent, ...actionHits, ...moduleHits, ...entityGroups.flatMap((g) => g.items)].forEach((e) =>
    all.set(e.id, e),
  );

  const submit = (id: string) => {
    const entry = all.get(id);
    if (!entry) return;
    if (id.startsWith("nav:") || id.startsWith("act:")) pushRecent(id);
    entry.run();
    setQuery("");
    combobox.closeDropdown();
    inputRef?.current?.blur();
    onDone?.();
  };

  const option = (e: Entry, key = e.id) => (
    <Combobox.Option value={e.id} key={key}>
      <Group gap="sm" wrap="nowrap">
        <e.icon size={18} stroke={1.6} style={{ flexShrink: 0, opacity: 0.8 }} aria-hidden />
        <div style={{ minWidth: 0, flex: 1 }}>
          <Text size="sm" truncate>
            {e.label}
          </Text>
          {e.hint && (
            <Text size="xs" c="dimmed" truncate>
              {e.hint}
            </Text>
          )}
        </div>
        {e.right}
      </Group>
    </Combobox.Option>
  );

  const nothing =
    q.length > 0 &&
    moduleHits.length === 0 &&
    actionHits.length === 0 &&
    entityGroups.length === 0 &&
    !entities.isFetching;

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={submit}
      withinPortal={!alwaysOpen}
      position="bottom-start"
      offset={6}
      shadow="md"
      width={alwaysOpen ? undefined : 460}
    >
      <Combobox.Target>
        <TextInput
          ref={inputRef}
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => {
            setQuery(e.currentTarget.value);
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
          onFocus={() => combobox.openDropdown()}
          onClick={() => combobox.openDropdown()}
          onBlur={() => !alwaysOpen && combobox.closeDropdown()}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setQuery("");
              combobox.closeDropdown();
              e.currentTarget.blur();
              onDone?.();
            }
          }}
          placeholder={t("search.placeholder")}
          aria-label={t("search.placeholder")}
          leftSection={<IconSearch size={16} />}
          rightSection={
            entities.isFetching ? (
              <Loader size="xs" />
            ) : alwaysOpen ? null : (
              <Kbd size="xs">{os === "macos" ? "⌘K" : "Ctrl K"}</Kbd>
            )
          }
          rightSectionWidth={alwaysOpen ? undefined : 62}
          w={alwaysOpen ? "100%" : 380}
          radius="md"
        />
      </Combobox.Target>

      <Combobox.Dropdown hidden={alwaysOpen ? false : undefined}>
        <Combobox.Options>
          <ScrollArea.Autosize mah={alwaysOpen ? "calc(100dvh - 90px)" : 440} type="scroll">
            {recent.length > 0 && (
              <Combobox.Group label={t("search.group.recent")}>{recent.map((e) => option(e, `r-${e.id}`))}</Combobox.Group>
            )}
            {actionHits.length > 0 && (
              <Combobox.Group label={t("search.group.actions")}>{actionHits.map((e) => option(e))}</Combobox.Group>
            )}
            {entityGroups.map((g) => (
              <Combobox.Group key={g.key} label={g.label}>
                {g.items.map((e) => option(e))}
              </Combobox.Group>
            ))}
            {moduleHits.length > 0 && (
              <Combobox.Group label={t("search.group.modules")}>{moduleHits.map((e) => option(e))}</Combobox.Group>
            )}
            {nothing && <Combobox.Empty>{t("search.empty")}</Combobox.Empty>}
          </ScrollArea.Autosize>
          {!q && (
            <Group gap={6} px="sm" py={6} c="dimmed" justify="flex-end">
              <Badge variant="default" size="xs" tt="none">
                ↑↓
              </Badge>
              <Text size="xs">{t("search.hint.navigate")}</Text>
              <Badge variant="default" size="xs" tt="none">
                ↵
              </Badge>
              <Text size="xs">{t("search.hint.open")}</Text>
            </Group>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
